import { ArrowDropDown, ArrowDropUp } from '@mui/icons-material'
import type { TextFieldVariants } from '@mui/material'
import { Box, IconButton, InputAdornment } from '@mui/material'
import { TextField } from 'components/@extended/TextField'
import React from 'react'
import { NumericFormat } from 'react-number-format'

interface AllocationSelectorProps {
  value: number // value as a decimal (e.g., 0.1234 for 12.34%)
  setValue: (newValue: number) => void
  min?: number // as decimal (e.g., 0.1 for 10%)
  max?: number // as decimal (e.g., 0.5 for 50%)
  step?: number // as decimal (e.g., 0.01 for 1%)
  disabled?: boolean
  readonly?: boolean
  reviewOnly?: boolean
  fontSize?: string
  decimalPlaces?: number
  variant?: TextFieldVariants
  pill?: boolean
  shrink?: boolean
  name?: string
  label?: string
  style?: React.CSSProperties
}

const AllocationSelector: React.FC<AllocationSelectorProps> = ({
  value,
  setValue,
  min = 0,
  max = 1,
  step = 0.0001,
  disabled = false,
  readonly = false,
  reviewOnly = false,
  fontSize = '1.1rem',
  decimalPlaces = 2,
  variant = 'standard',
  pill = false,
  shrink = false,
  name = 'allocation',
  label,
  style,
}) => {
  // Convert decimal to percent for display
  const displayValue = (Number(value) * 100).toFixed(decimalPlaces)

  const handleChange = (event: { target: { name: string; value: string } }) => {
    // Convert the percentage back to a decimal
    const decimalValue = Number.parseFloat(event.target.value) / 100
    if (!isNaN(decimalValue) && decimalValue >= min && decimalValue <= max) {
      setValue(Number(decimalValue.toFixed(decimalPlaces + 2)))
    }
  }

  const adjustValue = (increment: boolean) => {
    let newValue = increment ? value + step : value - step
    newValue = Math.max(min, Math.min(max, Number(newValue.toFixed(decimalPlaces + 2))))
    setValue(newValue)
  }

  const epsilon = 1e-8

  const isAtMax = value >= max - epsilon
  const isAtMin = value <= min + epsilon

  const effectiveDisabled = disabled || readonly

  if (reviewOnly) {
    return (
      <Box display="flex" alignItems="center">
        <span style={{ fontSize }}>{(Number(value) * 100).toFixed(decimalPlaces)}%</span>
      </Box>
    )
  }

  return (
    <Box display="flex" alignItems="center" style={style}>
      <TextField
        name={name}
        label={label}
        value={displayValue}
        onChange={handleChange}
        variant={variant}
        pill={pill}
        shrink={shrink}
        disabled={effectiveDisabled}
        InputProps={{
          inputComponent: NumericFormat as any,
          inputProps: {
            decimalScale: decimalPlaces,
            fixedDecimalScale: true,
            min: (min * 100).toFixed(decimalPlaces),
            max: (max * 100).toFixed(decimalPlaces),
            step: step * 100,
            style: { textAlign: 'right', width: 70, fontSize },
          },
          endAdornment: <InputAdornment position="end">%</InputAdornment>,
        }}
        sx={{ width: '110px', fontSize }}
      />
      <Box display="flex" flexDirection="column" ml={1}>
        <IconButton
          size="small"
          onClick={() => adjustValue(true)}
          disabled={effectiveDisabled || isAtMax}
          style={{ padding: 2 }}
        >
          <ArrowDropUp />
        </IconButton>
        <IconButton
          size="small"
          onClick={() => adjustValue(false)}
          disabled={effectiveDisabled || isAtMin}
          style={{ padding: 2 }}
        >
          <ArrowDropDown />
        </IconButton>
      </Box>
    </Box>
  )
}

export default AllocationSelector
